// @ts-check

import { api } from '@/api'
import { createCustomer } from '@/api/customers'
import { getIncomeById } from '@/api/incomes'
import {
  createInvoice,
  createInvoiceItem,
  getInvoiceItems,
  getInvoicePreview,
  getPaymentTerms,
  sendInvoice,
  updateInvoice,
  updateInvoiceItem
} from '@/api/invoice'
import { FORMAT_VALUE_DATE } from '@/helpers/dates'
import { addOrUpdate } from '@/helpers/functional'
import { serialize as serializeCustomer } from '@/serializers/customer'
import { serializeInvoiceEntry } from '@/serializers/invoice'
import { format, isBefore } from 'date-fns'
import invariant from 'invariant'
import * as _ from 'lodash-es'
import { v4 as createUuid } from 'uuid'
import { getField, updateField } from 'vuex-map-fields'

export const INVOICE_VAT_REPLACEMENT_DATE = '2024-09-01'

const $createEmptyEntry = () => {
  return {
    id: null,
    attachments: [],
    canToggleDebtCollection: false,
    /** @type {CustomerResource} */
    customer: {
      id: null,
      type: 0, // company 0, person 1
      business_id: null,
      company_name: null, // Company only
      first_name: null, // Person only
      last_name: null, // Person only
      vat_number: null,
      contact_name: null, // Required if company
      email: null, // Required if delivery = email
      phone: null,
      e_invoice_address: null, // Required if delivery = finvoice
      e_invoice_operator: null,
      shipping: 'email',
      address: {
        address: null,
        address_2: null,
        zip: null,
        post_office: null,
        country: 'FI'
      }
    },
    /** @type {InvoiceResource} */
    invoice: {
      id: null,
      vat_0_code_id: null,
      payment_term_days: 14,
      delivery_date: null,
      billing_date: null,
      note_time: 7,
      order_number: null,
      late_payment_interest: 7,
      invoice_number: null,
      currency: 'EUR',
      language: 'fi',
      invoice_date: format(new Date(), FORMAT_VALUE_DATE), // Date string($date)
      due_date: null,
      our_reference: null,
      customer_reference: null,
      message: null,
      email_message: null,
      comments: null,
      shipping: null,
      automatic_debt_collection_enabled: false,
      apply_for_financing: false
    },
    items: [],
    /** @type {InvoiceTotals} */
    invoice_totals: {
      total_without_vat: 0,
      total_vat_amount: 0,
      total_with_vat: 0
    }
  }
}

/** @returns {InvoiceModuleState} */
export const initInvoiceState = () => ({
  paymentTerms: [],
  ...$createEmptyEntry(),
  previewFile: null
})

/** @type {InvoiceGetterTree} */
export const invoiceGetters = {
  getField,
  entry: (state) => {
    return {
      id: state.id,
      customer: state.customer,
      invoice: state.invoice,
      items: state.items
    }
  },
  invoiceLanguage: ({ invoice }) => invoice?.language ?? null,
  customer: ({ customer }) => customer ?? null,
  invoice: ({ invoice }) => invoice ?? null,
  items: ({ items }) => items ?? null,
  description: (state) => state.invoice.message,
  deliveryMethod: (state) => state.customer.shipping,
  deliveryDate: (state) => state.invoice.delivery_date,
  billingDate: (state) => state.invoice.billing_date,
  incomeId: (state) => state.id,
  hasZeroVatItems: (state) => {
    return state.items.some((item) => {
      return item.vat === 0
    })
  },
  invoiceTotals: (state) => {
    return state.invoice_totals
  },
  isTotalAmountNegativeOrZero: (state) => {
    return (
      state.items.some((item) => item.id) &&
      state.invoice_totals.total_without_vat <= 0
    )
  },
  invoiceFinancingCriteriaMet: (state) => {
    return (
      state.invoice_totals.total_without_vat >= 600 && state.customer.type === 0
    )
  },
  isInvoiceShippingMethodFinanced: (state) => {
    return state.invoice.shipping === 'financing'
  }
}

/** @type {InvoiceMutationTree} */
export const invoiceMutations = {
  updateField,
  setItems: (state, items) => {
    state.items = _.map(items, (item) => ({
      uuid: createUuid(),
      ...item
    }))
  },
  setAttachments: (state, attachments) => {
    state.attachments = attachments
  },
  setInvoiceLanguage: (state, language) => {
    state.invoice.language = language
  },
  setInvoice: (state, invoice) => {
    state.invoice = invoice
  },
  setInvoiceTotals: (state, totals) => {
    state.invoice_totals = totals
  },
  setInvoiceId: (state, invoiceId) => {
    if (!state.invoice.id) {
      state.invoice.id = invoiceId
    }
  },
  setCanToggleDebtCollection: (state, canToggle) => {
    state.canToggleDebtCollection = canToggle
  },
  updateCustomer: (state, updatedCustomer) => {
    state.customer = { ...state.customer, ...updatedCustomer }

    if (updatedCustomer.language) {
      state.invoice.language = updatedCustomer.language
    }
  },
  addItem: (state, item) => {
    state.items = _.concat(state.items, { uuid: createUuid(), ...item })
  },
  setCustomerId: (state, customerId) => {
    state.customer.id = customerId
  },
  setCustomer: (state, customer) => {
    state.customer = customer
  },
  updateEntryId: (state, id) => {
    state.id = id
  },
  setPaymentTerms: (state, terms) => {
    state.paymentTerms = terms
  },
  setPreview: (state, preview) => {
    state.previewFile = preview
  },
  setDescription: (state, description) => {
    state.invoice.message = description
  },
  removeItem: (state, { id, uuid }) => {
    const idToFind = id || uuid

    const foundIdx = state.items.findIndex((item) => {
      return item.id === idToFind || item.uuid === idToFind
    })

    if (foundIdx === -1) {
      return
    }

    state.items.splice(foundIdx, 1)
  },

  clearAllData: (state) => {
    Object.assign(state, initInvoiceState())
  }
}

/** @type {InvoiceActionTree} */
export const invoiceActions = {
  async init({ commit, dispatch }, id) {
    commit('clearAllData')

    if (id) {
      const income = await getIncomeById(id)

      invariant(income, 'No income found')

      const deserializedData = income

      const { customer } = deserializedData

      const { invoice } = deserializedData

      customer.shipping = customer.shipping || income.invoice.shipping
      invoice.payment_term_days = income.invoice.payment_term_days ?? 0
      invoice.invoice_date = income.date
      invoice.due_date = income.due_date

      commit('setCustomer', customer)
      commit('setDescription', income.description)
      commit('updateEntryId', income.id)
      commit('setInvoice', invoice)
      commit('setAttachments', income.attachments)
      await dispatch('updateInvoiceItems')
    }

    await Promise.all([dispatch('fetchPaymentTerms')])
  },
  async updateInvoiceItems({ state, commit }) {
    invariant(state.id, 'No invoice id')
    const { items, totals } = await getInvoiceItems(state.id)
    commit('setItems', items)
    commit('setInvoiceTotals', totals)
    return items
  },
  async createMissingInvoice({ state, commit, dispatch, getters }) {
    if (state.id) {
      return
    }

    const stateCustomerPaymentTermId = state.customer.payment_term_id

    let paymentTermDays = null

    // Use customer payment term days instead of drafted ones.
    if (stateCustomerPaymentTermId) {
      if (!state.paymentTerms.length) {
        await dispatch('fetchPaymentTerms')
      }
      paymentTermDays =
        state.paymentTerms.find(({ id }) => id === stateCustomerPaymentTermId)
          ?.days || null
    }

    /**
     * Post and update state
     */
    const income = await createInvoice()
    const deserializedData = income

    const { invoice } = deserializedData

    if (paymentTermDays === null) {
      paymentTermDays = invoice.payment_term_days || 0
    }

    commit('setDescription', income.description)
    commit('updateEntryId', income.id)
    commit('setInvoice', {
      ...getters.invoice,
      invoice_date: income.date,
      due_date: income.due_date,
      payment_term_days: paymentTermDays
    })
  },
  async save({ state, getters }) {
    invariant(state.id, 'No invoice id')
    return updateInvoice(state.id, serializeInvoiceEntry(getters.entry))
  },
  saveCustomerDataIfNew({ state, commit, getters }) {
    let saveCustomerDataIfNewPromise = true

    if (!state.customer.id) {
      saveCustomerDataIfNewPromise = createCustomer(
        serializeCustomer({
          ...state.customer,
          language: getters.invoiceLanguage
        })
      ).then(({ data }) => {
        commit('setCustomerId', data.id)
      })
    }

    return saveCustomerDataIfNewPromise
  },
  async advance({ dispatch, commit }) {
    await dispatch('createMissingInvoice')
    await dispatch('saveCustomerDataIfNew')
    const res = await dispatch('save')
    commit('setInvoiceId', res.invoice?.id)
    commit(
      'setCanToggleDebtCollection',
      res.actions?.can_debt_collection_be_toggled ?? false
    )
  },
  async saveItem({ state, commit }, itemToSave) {
    invariant(state.id, 'No invoice id')

    const { item: savedItem, totals } = await (itemToSave.id
      ? updateInvoiceItem(state.id, itemToSave.id, itemToSave)
      : createInvoiceItem(state.id, itemToSave))

    const savedItemWithUuid = {
      ...savedItem,
      uuid: itemToSave.uuid
    }

    commit('setItems', addOrUpdate(state.items, savedItemWithUuid, 'uuid'))
    commit('setInvoiceTotals', totals)

    return savedItem
  },
  async deleteItem({ state, commit }, id) {
    const res = await api.delete(`incomes/${state.id}/invoices/items/${id}`)

    commit('removeItem', { id })

    if (res) {
      commit('setInvoiceTotals', res.invoice_totals)
    } else {
      commit('setInvoiceTotals', null)
    }
  },
  async fetchPaymentTerms({ commit }) {
    const paymentTerms = await getPaymentTerms()
    const mappedTerms = paymentTerms.map(({ id, days }) => ({
      id,
      days,
      value: days
    }))
    commit('setPaymentTerms', mappedTerms)
  },

  async fetchPreview({ commit, state }) {
    invariant(state.id, 'No invoice id')
    const preview = await getInvoicePreview(state.id)
    commit('setPreview', preview)
    return preview
  },

  async send({ state, dispatch }) {
    invariant(state.id, 'No invoice id')

    await sendInvoice(state.id)

    if (state.customer?.shipping === 'download' && state.id) {
      await dispatch('incomes/downloadInvoice', state.id, { root: true })
    }

    await dispatch('companyData/getUserCompany', null, { root: true })
  },
  createEmptyItem({ commit, getters, rootGetters }) {
    const defaultVat = isBefore(
      getters.deliveryDate,
      INVOICE_VAT_REPLACEMENT_DATE
    )
      ? 24
      : 25.5

    commit('addItem', {
      title: '',
      description: null,
      quantity: 1,
      unit: null,
      unit_price: 0,
      price_include_vat: false,
      discount: 0,
      row_total_vat_amount: 0,
      row_total_with_vat: 0,
      row_total_without_vat: 0,
      uuid: createUuid(),
      vat:
        getters.hasZeroVatItems || !rootGetters['companyData/isVatLiable']
          ? 0
          : defaultVat
    })
  }
}

export const invoiceModule = {
  namespaced: true,
  state: initInvoiceState,
  getters: invoiceGetters,
  actions: invoiceActions,
  mutations: invoiceMutations
}

export default invoiceModule
